import { getAppStore } from '@src/utils/store-reference';
import { useQueryClient } from '@tanstack/react-query';
import { NonPcnQueries, NON_PCN_QUERY_KEYS } from '../query';
import { PROJECT_QUERY_KEYS } from '@src/api/services/project';
import { useFormStore, useNonPcn, useRequest, useJpa, useApp } from '@stores';
import { RequestType, StorageScopes } from '@src/utils/enums';
import type { AxiosError } from 'axios';
import { GetNonPcnResponseData } from '../types';
import { useNavigate } from '@tanstack/react-router';

export type FetchNonPcnParams = {
  projectId: number;
  requestId: number;
  version: number;
  requestStatus: string;
  isGenerate: boolean;
  archived?: boolean;
};

export const useNonPcnRequestHandler = () => {
  const queryClient = useQueryClient();
  const store = getAppStore();
  const { resetToDefaults: resetJpaToDefaults } = useJpa.getState();
  const { setNonPcnRequest } = useNonPcn.getState();
  const { setApiAnswers } = useFormStore.getState();
  const { setIsLoading } = useApp.getState();
  const { setSelectedRequestId, resetRequest } = useRequest.getState();
  const navigate = useNavigate();

  const handleError = (err: AxiosError) => {
    console.error(err);
    setIsLoading(false);
    return null;
  };

  const fetchNonPcn = async ({
    isGenerate,
    projectId: projectID,
    requestId: requestID,
    requestStatus,
    version,
    archived,
  }: FetchNonPcnParams) => {
    !isGenerate &&
      setIsLoading(true, `Retrieving ${RequestType.NonPreconstructionNotification} Request Information...`);

    resetRequest();
    resetJpaToDefaults();

    const queryKey = [NON_PCN_QUERY_KEYS.GET_NON_PCN, projectID, requestID, version];

    queryClient.invalidateQueries({ queryKey });

    const data = await queryClient
      .fetchQuery({
        queryKey,
        queryFn: () => NonPcnQueries.getNonPcn(projectID, requestID, version),
        networkMode: 'always',
        retry: false,
      })
      .catch(handleError);

    const responseData = { ...data, archived };
    const nonPcnRequestID = responseData?.request?.nonPCNRequests?.[0]?.nonPcnRequestID;

    setIsLoading(false);

    if (!responseData) return;

    const selectedNonPcn = responseData?.request?.nonPCNRequests?.find(
      (item) => item.nonPcnRequestID === nonPcnRequestID
    );

    queryClient.setQueryData(queryKey, (oldData: GetNonPcnResponseData) => ({
      ...(oldData ?? {}),
      ...responseData,
      selectedNonPcnRequest: selectedNonPcn,
    }));
    queryClient.invalidateQueries({
      queryKey: [
        PROJECT_QUERY_KEYS.GET_SHARED_CONTACTS,
        store.selectProjectID(),
        store.selectRequestID(),
        store.selectVersion(),
      ],
    });

    useJpa.setState({ jpaBasicInfo: responseData?.request?.jpaBasicInfo });
    store.doUpdateRequestAPIData(responseData);
    store.doUpdateRequestData(responseData);
    store.doUpdateRequestObject({ requestStatus });

    setNonPcnRequest(selectedNonPcn);
    setApiAnswers(responseData);

    if (isGenerate) {
      store.doGenerateDraftDocuments();
    } else {
      const nonPcnRequestId = responseData?.request?.nonPCNRequests?.[0]?.nonPcnRequestID;
      setSelectedRequestId('11');
      await navigate({
        to: '/forms/$id',
        params: { id: String(requestID) },
      });
      await store.doFetchUserFilesDetails({
        componentID: nonPcnRequestId,
        scope: StorageScopes.ProjectFolder,
      });
    }
    return responseData;
  };
  return { fetchNonPcn };
};
